import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useParams } from "react-router-dom";

import { calculateSpanStatuses, type SpanStatus } from "../utils/calculateSpanStatus";

import { useTraceTable, type SpanTableRow } from "./useTraceTable";

import useWorkflowData from "@/components/Workflows/WorkflowVersion/hooks/useWorkflowData";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useTraceSpans, type TraceEntity, type TraceIOPreview } from "@/queries";
import type { Span, SpanNode } from "@/types/spans";

type TimeRange = [number, number];

const DEFAULT_TIME_RANGE: TimeRange = [0, 100];

/** Pad OTEL hex IDs so unpadded parent_id values still resolve to padded span_ids. */
const normalizeOtelHexId = (value: string | null | undefined, length: number): string | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (!/^[0-9a-fA-F]+$/.test(value) || value.length > length) {
    return value;
  }
  return value.padStart(length, "0");
};

const buildSpanTree = (spans?: Span[]): SpanNode[] | null => {
  if (!spans) {
    return null;
  }

  const spanMap = new Map<string, SpanNode>();
  const rootNodes: SpanNode[] = [];
  const assignedChildren = new Set<string>();

  spans.forEach(span => {
    const spanId = normalizeOtelHexId(span.span_id, 16) ?? span.span_id;
    spanMap.set(spanId, {
      ...span,
      span_id: spanId,
      parent_id: normalizeOtelHexId(span.parent_id, 16),
      children: [],
    });
  });

  spanMap.forEach(span => {
    if (span.parent_id === null) {
      rootNodes.push(span);
      return;
    }

    const parent = spanMap.get(span.parent_id);
    if (!parent || parent === span) {
      rootNodes.push(span);
      return;
    }

    parent.children.push(span);
    assignedChildren.add(span.span_id);
  });

  spanMap.forEach(span => {
    if (span.parent_id === null || assignedChildren.has(span.span_id)) {
      return;
    }

    if (!rootNodes.some(rootNode => rootNode.span_id === span.span_id)) {
      rootNodes.push(span);
    }
  });

  const sortSpans = (nodes: SpanNode[]): SpanNode[] => {
    nodes.sort((left, right) => {
      const byMs = new Date(left.start).getTime() - new Date(right.start).getTime();
      if (byMs !== 0) {
        return byMs;
      }
      return left.start.localeCompare(right.start) || left.span_id.localeCompare(right.span_id);
    });

    nodes.forEach(node => {
      node.children = sortSpans(node.children);
    });

    return nodes;
  };

  return sortSpans(rootNodes);
};

const getTraceMetrics = (spans?: Span[]) => {
  if (!spans?.length) {
    return {
      traceStartTime: 0,
      traceDuration: 0,
    };
  }

  const startTimes = spans.map(span => new Date(span.start).getTime());
  const endTimes = spans
    .filter(span => span.end !== null)
    .map(span => new Date(span.end!).getTime());

  const traceStart = Math.min(...startTimes);
  const traceEnd = endTimes.length > 0 ? Math.max(...endTimes) : Date.now();

  return {
    traceStartTime: traceStart,
    traceDuration: traceEnd - traceStart,
  };
};

const flattenSpans = (nodes: SpanNode[] | null): SpanNode[] => {
  if (!nodes) {
    return [];
  }

  const flattened: SpanNode[] = [];

  const visitNode = (node: SpanNode) => {
    flattened.push(node);
    node.children.forEach(visitNode);
  };

  nodes.forEach(visitNode);

  return flattened;
};

export interface TraceDetailState {
  traceId?: string;
  error?: Error | null;
  loggedBeforeRetentionPolicy: boolean;
  flattenedSpans: SpanNode[];
  isHighlighted: (row: SpanTableRow) => boolean;
  isLoading: boolean;
  isWorkflow: boolean;
  selectedSpan: SpanNode | null;
  setSelectedSpan: Dispatch<SetStateAction<SpanNode | null>>;
  traceInput: TraceIOPreview | null;
  traceFinalOutput: TraceIOPreview | null;
  traceEntities: TraceEntity[];
  setTimeRange: Dispatch<SetStateAction<TimeRange>>;
  spanStatuses: Map<string, SpanStatus>;
  spanTree: SpanNode[] | null;
  table: ReturnType<typeof useTraceTable>;
  timeRange: TimeRange;
  userToken: string;
  workflow: ReturnType<typeof useWorkflowData>["workflow"];
}

export const useTraceDetailState = (traceIdOverride?: string): TraceDetailState => {
  const [selectedSpan, setSelectedSpan] = useState<SpanNode | null>(null);
  const [timeRange, setTimeRange] = useState<TimeRange>(DEFAULT_TIME_RANGE);
  const hasAutoSelectedRef = useRef(false);

  const { traceId: routeTraceId } = useParams<{ traceId: string }>();
  const traceId = traceIdOverride ?? routeTraceId;
  const authContext = useAuth();
  const userContext = useUser();
  const userToken = authContext!.userToken!;

  const { data, isLoading, error } = useTraceSpans({
    userToken,
    traceId: traceId!,
    workspace_id: userContext.activeWorkspaceId!,
  });

  const spanTree = useMemo(() => buildSpanTree(data?.spans), [data?.spans]);

  useEffect(() => {
    setSelectedSpan(null);
    setTimeRange(DEFAULT_TIME_RANGE);
    hasAutoSelectedRef.current = false;
  }, [traceId]);

  useEffect(() => {
    if (!spanTree?.length || hasAutoSelectedRef.current) {
      return;
    }

    setSelectedSpan(spanTree[0]);
    hasAutoSelectedRef.current = true;
  }, [spanTree]);

  const flattenedSpans = useMemo(() => flattenSpans(spanTree), [spanTree]);
  const { traceStartTime, traceDuration } = useMemo(
    () => getTraceMetrics(data?.spans),
    [data?.spans],
  );

  const table = useTraceTable({
    spanTree,
    timeRange,
    traceStartTime,
    traceDuration,
  });

  const spanStatuses = useMemo(() => calculateSpanStatuses(spanTree), [spanTree]);

  const selectedSpanId = selectedSpan?.span_id ?? null;
  const isHighlighted = useCallback(
    (row: SpanTableRow) => selectedSpanId === row.span_id,
    [selectedSpanId],
  );

  const { workflow } = useWorkflowData(
    {
      workflowId: selectedSpan?.attributes?.workflow_id,
      versionNumber: selectedSpan?.attributes?.workflow_version,
      userToken,
      workspaceId: selectedSpan?.attributes?.workspace_id,
    },
    false,
  );

  const isWorkflow = Boolean(
    selectedSpan?.attributes?.workflow_id && selectedSpan?.attributes?.workflow_version && workflow,
  );

  useEffect(() => {
    const gridApi = table.getGridApi();
    if (!gridApi || !selectedSpan) {
      return;
    }

    let targetNode: Parameters<Parameters<typeof gridApi.forEachNode>[0]>[0] | null = null;
    gridApi.forEachNode(node => {
      if (node.data?.span_id === selectedSpan.span_id) {
        targetNode = node;
      }
    });

    if (!targetNode) {
      return;
    }

    const expandParents = (node: NonNullable<typeof targetNode>) => {
      let expandedCount = 0;
      let currentParent = node.parent;

      while (currentParent) {
        if (!currentParent.expanded) {
          currentParent.setExpanded(true);
          expandedCount += 1;
        }
        currentParent = currentParent.parent ?? null;
      }

      return expandedCount;
    };

    const expandedCount = expandParents(targetNode);
    const timeoutId = window.setTimeout(
      () => {
        let visibleTargetNode: typeof targetNode = null;
        gridApi.forEachNode(node => {
          if (node.data?.span_id === selectedSpan.span_id) {
            visibleTargetNode = node;
          }
        });

        if (visibleTargetNode) {
          gridApi.ensureNodeVisible(visibleTargetNode, "middle");
        }
      },
      expandedCount > 0 ? 200 : 0,
    );

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [selectedSpan, table]);

  return {
    traceId,
    error,
    loggedBeforeRetentionPolicy: Boolean(data?.logged_before_retention_policy),
    flattenedSpans,
    isHighlighted,
    isLoading,
    isWorkflow,
    selectedSpan,
    setSelectedSpan,
    traceInput: data?.trace_input ?? null,
    traceFinalOutput: data?.trace_final_output ?? null,
    traceEntities: data?.trace_entities ?? [],
    setTimeRange,
    spanStatuses,
    spanTree,
    table,
    timeRange,
    userToken,
    workflow,
  };
};
